<template lang="pug">
.v-main-filter.main-filter(v-if="data")
	.main-filter__tabs
		v-field(:field="data" :model-value="data.value" @update:model-value="requestDataLoad")

	.main-filter__block(v-if="data.fields")
		.main-filter__item(v-for="(field, index) in data.fields"
			:key="index"
			:class="{'main-filter__item--attention': showRequiredStageAttention && isNextRequiredField(field)}"
			:style="{width: getFieldWidth(field)}")
			template(v-if="field.type === 'range'" )
				v-price-ranger(:field="field" :model-value="field.value" @update:model-value="updateFilters(field, $event)")
			template(v-else )
				v-field(:field="field" :model-value="field.value" @update:model-value="updateFilters(field, $event)" @map-filter-changed="sendMapFilter")

	.main-filter__buttons(v-if="data.links" )
		a.main-filter__button(v-for="(link, index) in data.links"
			:key="index"
			:href="link.href"
			:class="[link.className, isSearchLink(link) ? 'main-filter__button--search' : 'main-filter__button--map', isFilterActionLink(link) && !hasCompletedRequiredStages ? ['btn--disabled', 'main-filter__button--sequence-required'] : []]"
			:aria-disabled="isFilterActionLink(link) && !hasCompletedRequiredStages ? 'true' : null"
			@click="handleLinkClick($event, index)")
			.main-filter__button-text(v-if="link.text" ) {{ link.text }}
			v-svg-icon.main-filter__button-icon(v-if="link.icon" :icon="link.icon")

</template>

<script>
import sourceMixin from '../components/mixins/v-mixin-source.vue';
import isMobMixin from '../components/mixins/v-mixin-is-mob.vue'
export default {

	mixins: [
		sourceMixin,
		isMobMixin
	],

	data() {
		return {
			data: null,
			localValue: null,
			tabName: null,
			filter: {},
			area: null,
			pendingLinkIndex: null,
			filterRequestFailed: false,
			requiredStageValidationAttempted: false
		};
	},

	computed: {
		requiredStageFields() {
			return this.data?.fields?.filter(field => field.stageRequired) || [];
		},

		nextRequiredField() {
			return this.requiredStageFields.find(field => !this.isStageFieldComplete(field)) || null;
		},

		hasCompletedRequiredStages() {
			return this.nextRequiredField === null;
		},

		showRequiredStageAttention() {
			return this.requiredStageValidationAttempted && !this.hasCompletedRequiredStages;
		}
	},

	watch: {
		tabName() {
			// this.localValue = null;
			// this.filter = null
		},

		loading(value) {
			// If Search was clicked while the request was in flight and both attempts
			// failed, submit the selected values directly to the catalog.
			if (!value && this.filterRequestFailed && this.pendingLinkIndex !== null) {
				const index = this.pendingLinkIndex;
				this.pendingLinkIndex = null;
				this.submitFilterFallback(index);
			}
		}
	},

	methods: {
		getFieldWidth(field) {
			// The parent has a small transparent gap between rounded controls. Reducing
			// the basis prevents configured percentage widths from wrapping to a new row.
			return field?.width ? `calc(${field.width} - 2px)` : null;
		},

		dataSet(data) {
			this.data = data;
			this.tabName = data.value;
			this.filter = this.initFilter();
			this.localValue = {
				...this.filter,
				[`tab[${this.data.name}]`]: this.tabName
			};

			// A user can click Search before the AJAX response updates its href.
			// Continue that click only after the response supplies the current link.
			if (this.pendingLinkIndex !== null) {
				const link = data.links?.[this.pendingLinkIndex];
				this.pendingLinkIndex = null;
				if (this.hasCompletedRequiredStages && link?.href) {
					window.location.href = link.href;
				}
			}
		},

		handleLinkClick(event, index) {
			const link = this.data.links?.[index];
			if (this.isFilterActionLink(link) && !this.hasCompletedRequiredStages) {
				event.preventDefault();
				this.requiredStageValidationAttempted = true;
				this.$nextTick(() => {
					const control = this.$el.querySelector('.main-filter__item--attention .field-select__btn, .main-filter__item--attention .field-input');
					control?.focus();
					control?.scrollIntoView({behavior: 'smooth', block: 'center'});
				});
				return;
			}

			this.requiredStageValidationAttempted = false;
			if (!this.loading && !this.filterRequestFailed) {
				return;
			}

			event.preventDefault();
			this.pendingLinkIndex = index;

			// dataLoad has already retried a server error once. If both attempts failed,
			// use a normal POST so Search still opens results with the selected values.
			if (!this.loading && this.filterRequestFailed) {
				this.pendingLinkIndex = null;
				this.submitFilterFallback(index);
			}
		},

		loadFilterData(options) {
			this.filterRequestFailed = false;

			return this.dataLoad({
				...options,
				retryOnServerError: 1,
				onError: () => {
					this.filterRequestFailed = true;
				}
			});
		},

		submitFilterFallback(index) {
			const link = this.data.links?.[index];
			if (!link?.href) {
				return;
			}

			const form = document.createElement('form');
			form.method = 'POST';
			form.action = link.href;
			form.hidden = true;

			const appendInput = (name, value) => {
				const input = document.createElement('input');
				input.type = 'hidden';
				input.name = name;
				input.value = value == null ? '' : value;
				form.append(input);
			};

			Object.entries(this.filter).forEach(([name, value]) => {
				const fieldMatch = name.match(/^filter\[([^\]]+)]$/);
				if (!fieldMatch) {
					return;
				}

				const valueName = `filter[${fieldMatch[1]}][value]`;
				if (Array.isArray(value)) {
					value.forEach((item) => appendInput(`${valueName}[]`, item));
				} else if (value && typeof value === 'object') {
					Object.entries(value).forEach(([key, item]) => appendInput(`${valueName}[${key}]`, item));
				} else {
					appendInput(valueName, value);
				}
			});

			document.body.append(form);
			form.submit();
		},

		initFilter() {
			let result = {};
			this.data.fields.forEach(field => {
				result[[`filter[${field.name}]`]] = field.value
			})
			return result
		},

		hasFieldValue(value) {
			if (Array.isArray(value)) {
				return value.some(item => item !== null && item !== undefined && item !== '');
			}

			return value !== null && value !== undefined && value !== '';
		},

		isStageFieldComplete(field) {
			if (field?.stageSatisfied) {
				return true;
			}

			const filterName = `filter[${field.name}]`;
			const value = Object.prototype.hasOwnProperty.call(this.filter, filterName)
				? this.filter[filterName]
				: field.value;

			return this.hasFieldValue(value);
		},

		isNextRequiredField(field) {
			return this.nextRequiredField?.name === field?.name;
		},

		isSearchLink(link) {
			return link?.icon === 'arrow-forward';
		},

		isFilterActionLink(link) {
			return this.isSearchLink(link) || link?.href?.includes('viewMode=map');
		},

		requestDataLoad(value) {
			this.tabName = value;
			this.requiredStageValidationAttempted = false;
			this.loadFilterData({
				url: this.data.remote,
				data: {[`tab[${this.data.name}]`]: value},
				delay: true
			})
		},

		updateFilters(field, value) {
			if (field.stageRequired) {
				field.stageSatisfied = false;
			}

			if (field.stageRole === 'region') {
				this.area = null;
				this.data.fields
					.filter(item => item.name !== field.name)
					.forEach(item => {
						item.stageSatisfied = false;
						delete this.localValue[`filter[${item.name}]`];
						this.filter[`filter[${item.name}]`] = null;
					});
			}

			if (field.stageRole === 'type') {
				this.data.fields
					.filter(item => !item.stageRequired)
					.forEach(item => {
						delete this.localValue[`filter[${item.name}]`];
						delete this.filter[`filter[${item.name}]`];
					});
			}

			this.localValue = {...this.localValue, ...{[`filter[${field.name}]`]: value}, ...{[`tab[${this.data.name}]`]: this.tabName}}

			this.filter = {...this.filter, [`filter[${field.name}]`]: value}
			if (field.stageRequired && this.hasFieldValue(value)) {
				this.requiredStageValidationAttempted = false;
			}

			this.loadFilterData({
				url: this.data.remote,
				data: {
					...this.localValue,
					mapFilter: this.area,
				},
				delay: true
			})
		},

		sendMapFilter(area) {
			this.area = area
			this.loadFilterData({
				url: this.data.remote,
				delay: true,
				data: {
					...{[`tab[${this.data.name}]`]: this.tabName},
					...this.filter,
					mapFilter: area,
					action: 'drawing'
				},
			})
		}
	}
};
</script>
